import { quotaRetryDelay } from "./quotaRetry";
/**
 * API Client for Nervedx EEG Application
 *
 * This is the CANONICAL HTTP client for all API requests in the application.
 * DO NOT create additional axios instances or use fetch() for API calls.
 *
 * ✅ Features:
 * - Automatic CSRF token injection for POST/PUT/PATCH/DELETE requests
 * - Automatic JWT token refresh on 401 responses with request queuing
 * - Cookie-based authentication (withCredentials: true)
 * - 10-second default timeout for standard requests
 * - Extended timeout for authentication handshakes under load
 * - Extended timeout for multipart/file upload requests
 * - Centralized error handling via response interceptors
 * - Request/response logging (development mode)
 *
 * 🔒 Security:
 * - CSRF tokens bootstrapped from `/csrf-token` responses and cached in memory
 * - Backend CSRF cookies stay httpOnly and are not read directly by the SPA
 * - Tokens automatically attached to state-changing requests
 * - Token refresh prevents session expiration during active use
 * - Automatic redirect to login on failed token refresh
 *
 * 📖 Usage Examples:
 *
 * @example GET request
 * ```typescript
 * import apiClient from '@shared/services/apiClient';
 *
 * const response = await apiClient.get('/users/me');
 * const user = response.data;
 * ```
 *
 * @example POST request (CSRF token automatically included)
 * ```typescript
 * import apiClient from '@shared/services/apiClient';
 *
 * const response = await apiClient.post('/patients', {
 *   first_name: 'John',
 *   last_name: 'Doe',
 *   date_of_birth: '1990-01-01'
 * });
 * const newPatient = response.data;
 * ```
 *
 * @example PUT request with error handling
 * ```typescript
 * import apiClient from '@shared/services/apiClient';
 *
 * try {
 *   const response = await apiClient.put(`/patients/${id}`, updatedData);
 *   return response.data;
 * } catch (error) {
 *   if (error.response?.status === 403) {
 *     console.error('Access denied');
 *   }
 *   throw error;
 * }
 * ```
 *
 * @example DELETE request
 * ```typescript
 * import apiClient from '@shared/services/apiClient';
 *
 * await apiClient.delete(`/patients/${id}`);
 * ```
 *
 * @example Request with query parameters
 * ```typescript
 * import apiClient from '@shared/services/apiClient';
 *
 * const response = await apiClient.get('/help/search', {
 *   params: { q: 'seizure detection' }
 * });
 * const results = response.data;
 * ```
 *
 * ⚠️ DO NOT USE:
 * - ❌ import axiosInstance from '@shared/utils/axiosConfig' (deprecated)
 * - ❌ import axiosInstance from '@shared/utils/axiosInstance' (deprecated)
 * - ❌ import { fetchWithAuth } from '@shared/utils/api' (insecure, deprecated)
 * - ❌ import axios from 'axios' (lacks CSRF protection and token refresh)
 *
 * @see {@link https://axios-http.com/docs/intro} Axios Documentation
 * @see Security Assessment Report in docs/root/security/SECURITY_ASSESSMENT_REPORT.md
 */

import axios, {
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import logger from "@shared/utils/logger";

type ApiRequestConfig = InternalAxiosRequestConfig & {
  _quotaRetries?: number;
  _retry?: boolean;
  _skipSessionRefresh?: boolean;
};

// Re-export commonly used axios types so consumers don't need to import axios directly
export type { AxiosResponse, AxiosError, AxiosRequestConfig };

/**
 * Dynamically determine the API base URL based on the current environment
 *
 * Logic:
 * 1. If VITE_API_URL is set and valid, use it
 * 2. If running on localhost/127.0.0.1, use relative /api (dev proxy)
 * 3. If on a domain, infer the API URL from the hostname:
 *    - app.staging.nervedx.com → https://api.staging.nervedx.com
 *    - app.nervedx.com → https://api.nervedx.com
 *    - portal.nervedx.com → https://api.nervedx.com
 * 4. Fallback to /api (relative path)
 */
function getApiBaseUrl(): string {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;

  // If explicitly configured and not localhost, use it
  if (
    envApiUrl &&
    !/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/.test(envApiUrl)
  ) {
    return envApiUrl;
  }

  // For localhost development, use relative path (Vite dev proxy)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1";

    if (isLocalhost) {
      return "/api";
    }

    // Production: infer API URL from current domain
    // app.staging.nervedx.com → https://api.staging.nervedx.com/api
    // app.nervedx.com → https://api.nervedx.com/api
    if (hostname.startsWith("app.")) {
      const apiHostname = hostname.replace(/^app\./, "api.");
      const protocol = window.location.protocol || "https:";
      return `${protocol}//${apiHostname}/api`;
    }
  }

  // Fallback to relative path
  return "/api";
}

const configuredApiBaseUrl = getApiBaseUrl();
const resolvedApiBaseUrl = configuredApiBaseUrl;
const DEFAULT_API_TIMEOUT_MS = 10000;
const AUTH_HANDSHAKE_TIMEOUT_MS = Number(
  import.meta.env.VITE_AUTH_HANDSHAKE_TIMEOUT_MS || 60 * 1000,
);
const configuredUploadTimeoutMs = import.meta.env.VITE_UPLOAD_TIMEOUT_MS;
export const UPLOAD_REQUEST_TIMEOUT_MS =
  configuredUploadTimeoutMs === undefined || configuredUploadTimeoutMs === ""
    ? 0
    : Number(configuredUploadTimeoutMs);

function isAuthHandshakeRequest(url: string, method: string): boolean {
  const normalized = String(url || "").replace(/\?.*$/, "").toLowerCase();
  if (!normalized) return false;
  if (method === "GET" && normalized.endsWith("/users/me")) return true;
  if (method !== "POST") return false;
  return (
    normalized.endsWith("/users/login") ||
    normalized.endsWith("/users/refresh-token")
  );
}

function isLongRunningUploadRequest(url: string, method: string): boolean {
  const normalized = String(url || "").replace(/\?.*$/, "");
  if (!normalized || method !== "POST") {
    return false;
  }
  return (
    /\/eeg\/upload\/[^/]+\/complete$/i.test(normalized) ||
    /\/eeg\/upload\/init$/i.test(normalized) ||
    /\/eeg\/direct-upload\/(?:complete|sign-parts|initiate|abort)$/i.test(
      normalized,
    ) ||
    /\/video-eeg\/upload\/[^/]+\/complete$/i.test(normalized)
  );
}
const SESSION_PROACTIVE_REFRESH_SKEW_MS = 30_000;
let authenticatedSessionHint = false;
let sessionExpiryHintMs: number | null = null;

export function setSessionRefreshState(input: {
  isAuthenticated: boolean;
  tokenExpiryMs?: number | null;
}): void {
  authenticatedSessionHint = input.isAuthenticated;
  sessionExpiryHintMs =
    input.isAuthenticated &&
    typeof input.tokenExpiryMs === "number" &&
    Number.isFinite(input.tokenExpiryMs) &&
    input.tokenExpiryMs > 0
      ? input.tokenExpiryMs
      : null;
  if (!input.isAuthenticated) {
    clearRefreshRetryCooldown();
  }
}

export function clearSessionRefreshState(): void {
  authenticatedSessionHint = false;
  sessionExpiryHintMs = null;
  clearRefreshRetryCooldown();
}

// Log the API URL in development for debugging
if (import.meta.env.DEV) {
  logger.debug(`[apiClient] API Base URL: ${resolvedApiBaseUrl}`);
}

const apiClient = axios.create({
  baseURL: resolvedApiBaseUrl,
  withCredentials: true, // Always send cookies
  timeout: DEFAULT_API_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
  },
});

function stripApiSuffix(url: string): string {
  return url.replace(/\/api\/?$/, "");
}

export function getRealtimeBaseUrl(): string {
  const currentBaseUrl = String(
    apiClient.defaults.baseURL || resolvedApiBaseUrl || "",
  ).trim();

  if (!currentBaseUrl || currentBaseUrl === "/api") {
    return typeof window !== "undefined" ? window.location.origin : "";
  }

  if (/^https?:\/\//.test(currentBaseUrl)) {
    return stripApiSuffix(currentBaseUrl);
  }

  return typeof window !== "undefined" ? window.location.origin : currentBaseUrl;
}

/**
 * Override the API base URL at runtime.
 *
 * Called after org resolution for enterprise tenants that have a dedicated
 * backend (`dedicated_api_url`). SMB tenants use the default URL determined
 * at startup from VITE_API_URL / hostname inference.
 *
 * Security note: the url is validated upstream (from the control-plane config
 * endpoint, not user input) and must be an HTTPS or relative URL.
 */
export function setApiBaseUrl(url: string): void {
  if (!url || typeof url !== "string") return;
  // Only allow https: or relative (/) origins — block data:, javascript:, etc.
  const trimmed = url.trim();
  if (trimmed && !/^(https?:\/\/|\/)/.test(trimmed)) {
    logger.warn("[apiClient] setApiBaseUrl rejected unsafe URL:", trimmed);
    return;
  }
  apiClient.defaults.baseURL = trimmed;
  // Reset the CSRF token cache so it is re-fetched from the new origin
  _csrfTokenCache = undefined;
  _csrfBootstrapPromise = null;
  if (import.meta.env.DEV) {
    logger.debug("[apiClient] API Base URL updated to:", trimmed);
  }
}

function isFormDataPayload(data: unknown): boolean {
  if (!data) return false;
  if (typeof FormData !== "undefined" && data instanceof FormData) return true;
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as Record<string, unknown>).append === "function" &&
    String(
      (data as { [Symbol.toStringTag]?: unknown })[Symbol.toStringTag] || "",
    ) === "FormData"
  );
}

function getHeaderValue(headers: unknown, key: string): string | undefined {
  if (!headers || typeof headers !== "object") return undefined;
  const normalizedKey = key.toLowerCase();
  const axiosHeaders = headers as {
    get?: (name: string) => string | undefined;
  };
  if (typeof axiosHeaders.get === "function") {
    return axiosHeaders.get(key) || axiosHeaders.get(normalizedKey);
  }
  const plain = headers as Record<string, unknown>;
  const direct = plain[key] ?? plain[normalizedKey];
  return typeof direct === "string" ? direct : undefined;
}

// In-memory CSRF token cache — populated from the /csrf-token response body.
// Needed when the app is served from a different subdomain than the API
// (e.g. app.staging.nervedx.com → api.staging.nervedx.com), because the
// csrfToken cookie is scoped to the API host and isn't readable via
// document.cookie from the app's origin.
let _csrfTokenCache: string | undefined;
export function setCsrfTokenCache(token: string): void {
  _csrfTokenCache = token;
}

let _csrfBootstrapPromise: Promise<string | undefined> | null = null;
let _csrfRefreshPromise: Promise<string | undefined> | null = null;

function isCsrfExemptRequest(url: string): boolean {
  const normalized = url.toLowerCase();
  return (
    normalized.includes("/csrf-token") ||
    normalized.includes("/users/login") ||
    normalized.includes("/users/register") ||
    normalized.includes("/users/refresh-token") ||
    normalized.includes("/billing/stripe/webhook")
  );
}

async function ensureCsrfToken(
  requestUrl: string,
): Promise<string | undefined> {
  if (_csrfTokenCache) return _csrfTokenCache;
  if (isCsrfExemptRequest(requestUrl)) return undefined;
  if (_csrfBootstrapPromise) return _csrfBootstrapPromise;

  _csrfBootstrapPromise = (async () => {
    try {
      const response = await axios.get(`${resolvedApiBaseUrl}/csrf-token`, {
        withCredentials: true,
        timeout: 5000,
      });
      const token = response.data?.csrfToken || response.data?.data?.csrfToken;
      if (typeof token === "string" && token.length > 0) {
        _csrfTokenCache = token;
      }
      return _csrfTokenCache;
    } catch {
      return undefined;
    } finally {
      // Only clear promise on failure - if we have a cached token, keep the
      // promise so that a cache clear + rapid concurrent calls still deduplicate
      if (!_csrfTokenCache) {
        _csrfBootstrapPromise = null;
      }
    }
  })();

  return _csrfBootstrapPromise;
}

/**
 * Refresh CSRF token with deduplication for retry scenarios.
 * Used when a request fails with CSRF_VALIDATION_FAILED to prevent
 * concurrent retries from creating conflicting _csrf secrets.
 */
async function refreshCsrfToken(): Promise<string | undefined> {
  if (_csrfRefreshPromise) return _csrfRefreshPromise;

  _csrfRefreshPromise = (async () => {
    try {
      _csrfTokenCache = undefined; // Clear stale cache
      _csrfBootstrapPromise = null; // Reset bootstrap promise
      const response = await axios.get(`${resolvedApiBaseUrl}/csrf-token`, {
        withCredentials: true,
        timeout: 5000,
      });
      const token = response.data?.csrfToken || response.data?.data?.csrfToken;
      if (typeof token === "string" && token.length > 0) {
        _csrfTokenCache = token;
      }
      return _csrfTokenCache;
    } catch {
      return undefined;
    } finally {
      _csrfRefreshPromise = null;
    }
  })();

  return _csrfRefreshPromise;
}

/**
 * Pre-fetch and cache the CSRF token using the shared deduplication mechanism.
 * Use this instead of apiClient.get("/csrf-token") to prevent concurrent
 * fetches that create conflicting _csrf secrets.
 */
export async function bootstrapCsrfToken(): Promise<string | undefined> {
  return ensureCsrfToken("/csrf-token");
}

export async function refreshAuthBootstrapToken(): Promise<string | undefined> {
  return refreshCsrfToken();
}

// Attach CSRF token for state-changing requests
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const requestConfig = config as ApiRequestConfig;
    const method = (config.method || "").toString().toUpperCase();
    const contentType = getHeaderValue(config.headers, "Content-Type") || "";
    const isUploadRequest =
      isFormDataPayload(config.data) ||
      contentType.toLowerCase().includes("multipart/form-data") ||
      isLongRunningUploadRequest(String(config.url || ""), method);

    // Browser auth model is cookie-first (httpOnly authToken/refreshToken).
    // Prevent stale bearer headers from overriding cookie-based session state.
    if (config.headers?.Authorization) {
      delete config.headers.Authorization;
    }

    await ensureFreshSessionBeforeRequest(requestConfig);

    // Keep normal API calls strict, but let auth complete during load spikes.
    if (
      isAuthHandshakeRequest(String(config.url || ""), method) &&
      (typeof config.timeout !== "number" ||
        config.timeout <= DEFAULT_API_TIMEOUT_MS)
    ) {
      config.timeout = AUTH_HANDSHAKE_TIMEOUT_MS;
    }

    // Keep normal API calls strict, but avoid timing out large file uploads.
    if (
      isUploadRequest &&
      (typeof config.timeout !== "number" ||
        config.timeout <= DEFAULT_API_TIMEOUT_MS)
    ) {
      config.timeout = UPLOAD_REQUEST_TIMEOUT_MS;
    }

    // Log outgoing requests for e2e diagnostics (Playwright traces capture console messages)
    try {
      const meta: Record<string, unknown> = {};
      if (config.params !== undefined) meta.params = config.params;
      if (config.data !== undefined) meta.data = config.data;
      logger.debug(
        `[api][request] ${method} ${config.baseURL || ""}${config.url}`,
        meta,
      );
    } catch {
      // ignore logging failures
    }
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      let csrfToken = _csrfTokenCache;
      if (!csrfToken) {
        csrfToken = await ensureCsrfToken(String(config.url || ""));
      }
      if (csrfToken) {
        config.headers.set("x-csrf-token", csrfToken);
      }
    }
    return config;
  },
);

// Capture CSRF token from /csrf-token responses and cache it in memory.
// This makes CSRF work even when document.cookie can't reach the API subdomain.
apiClient.interceptors.response.use((response) => {
  const url = response.config?.url || "";
  const token = response.data?.csrfToken || response.data?.data?.csrfToken;
  if (typeof token === "string" && token.length > 0) {
    _csrfTokenCache = token;
  } else if (url.includes("/csrf-token")) {
    _csrfTokenCache = undefined;
  }
  return response;
});

// Handle 401: try refresh, then retry original request
let isRefreshing = false;
let refreshPromise: Promise<AxiosResponse> | null = null;
const subscribers: Array<() => void> = [];
let authRedirectInProgress = false;
let refreshRetryBlockedUntilMs = 0;
let refreshRetryBlockReason: string | null = null;

function shouldSkipSessionRefresh(url: string): boolean {
  const normalized = url.toLowerCase();
  return (
    normalized.includes("/users/login") ||
    normalized.includes("/users/register") ||
    normalized.includes("/users/refresh-token") ||
    normalized.includes("/csrf-token")
  );
}

function getStoredSessionExpiryMs(): number | null {
  return sessionExpiryHintMs;
}

function shouldProactivelyRefreshSession(config: ApiRequestConfig): boolean {
  if (typeof window === "undefined") return false;
  if (config._skipSessionRefresh) return false;

  const requestUrl = String(config.url || "");
  if (!requestUrl || shouldSkipSessionRefresh(requestUrl)) {
    return false;
  }

  const currentPath = window.location.pathname || "";
  if (isAuthOrPublicRoute(currentPath)) return false;

  const hasStoredSession = authenticatedSessionHint;
  if (!hasStoredSession) return false;

  if (refreshRetryBlockedUntilMs > Date.now()) return false;

  const expiryMs = getStoredSessionExpiryMs();
  if (!expiryMs) return false;

  return expiryMs - Date.now() <= SESSION_PROACTIVE_REFRESH_SKEW_MS;
}

function startRefreshRequest(): Promise<AxiosResponse> {
  isRefreshing = true;
  refreshPromise = apiClient
    .post(
      "/users/refresh-token",
      {},
      {
        timeout: 5000,
        _skipSessionRefresh: true,
      } as AxiosRequestConfig & { _skipSessionRefresh: boolean },
    )
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  return refreshPromise;
}

export async function refreshAuthenticatedSession(): Promise<boolean> {
  if (refreshRetryBlockedUntilMs > Date.now()) {
    logger.debug("[api][refresh] Skipping — refresh is in Retry-After cooldown", {
      blockedUntil: new Date(refreshRetryBlockedUntilMs).toISOString(),
      reason: refreshRetryBlockReason,
    });
    return false;
  }

  try {
    logger.debug("[api][refresh] Starting session refresh");
    const response = refreshPromise
      ? await refreshPromise
      : await startRefreshRequest();

    const status = (response as AxiosResponse | undefined)?.status;
    logger.debug("[api][refresh] Refresh response received", { status });

    persistRefreshedSession((response as AxiosResponse | undefined)?.data);
    clearRefreshRetryCooldown();
    onRefreshed();
    return true;
  } catch (error) {
    const refreshError = error as AxiosError;
    logger.debug("[api][refresh] Refresh failed", {
      status: refreshError.response?.status,
      message: refreshError.message,
    });

    if (refreshError.response?.status === 429) {
      const retryAfterSeconds =
        extractRetryAfterSeconds(refreshError) || 60;
      setRefreshRetryCooldown(retryAfterSeconds, "refresh_rate_limited");
      logger.warn(
        "[api][refresh] Realtime refresh rate-limited; honoring Retry-After cooldown",
        { retryAfterSeconds },
      );
    }
    return false;
  }
}

/**
 * Run an account-security read with one explicit cookie-session recovery pass.
 * This covers route-mount races where AuthContext has not populated the
 * interceptor's authenticated-session hint before MFA/passkey panels load.
 */
export async function withAuthenticatedSessionRecovery<T>(
  request: () => Promise<T>,
): Promise<T> {
  try {
    logger.debug("[api][session-recovery] Attempting request");
    return await request();
  } catch (error) {
    const requestError = error as AxiosError;
    logger.debug("[api][session-recovery] Request failed", {
      status: requestError.response?.status,
      url: requestError.config?.url,
      message: requestError.message,
    });

    if (requestError.response?.status !== 401) {
      throw error;
    }

    logger.debug("[api][session-recovery] 401 detected — attempting session refresh");
    const refreshed = await refreshAuthenticatedSession();
    logger.debug("[api][session-recovery] Session refresh result", { refreshed });

    if (!refreshed) {
      logger.debug("[api][session-recovery] Refresh failed — propagating original error");
      throw error;
    }

    logger.debug("[api][session-recovery] Refresh succeeded — retrying original request");
    return request();
  }
}

async function ensureFreshSessionBeforeRequest(
  config: ApiRequestConfig,
): Promise<void> {
  if (!shouldProactivelyRefreshSession(config)) return;

  try {
    const response = refreshPromise
      ? await refreshPromise
      : await startRefreshRequest();
    persistRefreshedSession((response as AxiosResponse | undefined)?.data);
    clearRefreshRetryCooldown();
    onRefreshed();
  } catch (error) {
    const refreshError = error as AxiosError;
    if (refreshError.response?.status === 429) {
      const retryAfterSeconds =
        extractRetryAfterSeconds(refreshError) || 60;
      setRefreshRetryCooldown(retryAfterSeconds, "refresh_rate_limited");
      logger.warn(
        "[api][refresh] Proactive refresh rate-limited; honoring Retry-After cooldown",
        { retryAfterSeconds },
      );
    }
  }
}

function parseRetryAfterHeaderSeconds(
  value: string | undefined,
): number | null {
  if (!value) return null;
  const asInt = Number.parseInt(value, 10);
  if (Number.isFinite(asInt) && asInt > 0) {
    return asInt;
  }
  const asDateMs = Date.parse(value);
  if (!Number.isNaN(asDateMs)) {
    const deltaSeconds = Math.ceil((asDateMs - Date.now()) / 1000);
    return deltaSeconds > 0 ? deltaSeconds : null;
  }
  return null;
}

function extractRetryAfterSeconds(error: AxiosError): number | null {
  const headerValue = getHeaderValue(error.response?.headers, "Retry-After");
  const fromHeader = parseRetryAfterHeaderSeconds(headerValue);
  if (fromHeader && fromHeader > 0) return fromHeader;

  const body = (error.response?.data || {}) as { retryAfter?: unknown };
  const retryAfterValue = Number(body.retryAfter);
  if (Number.isFinite(retryAfterValue) && retryAfterValue > 0) {
    return Math.ceil(retryAfterValue);
  }
  return null;
}

function setRefreshRetryCooldown(seconds: number, reason: string) {
  const clampedSeconds = Math.max(1, Math.min(seconds, 15 * 60));
  refreshRetryBlockedUntilMs = Date.now() + clampedSeconds * 1000;
  refreshRetryBlockReason = reason;
}

function clearRefreshRetryCooldown() {
  refreshRetryBlockedUntilMs = 0;
  refreshRetryBlockReason = null;
}

function isAuthOrPublicRoute(pathname: string): boolean {
  return (
    pathname.startsWith("/login") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password")
  );
}

function onRefreshed() {
  subscribers.forEach((cb) => cb());
  subscribers.length = 0;
}

function extractPositiveInteger(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return Math.floor(parsed);
}

function extractRefreshPayload(data: unknown): {
  tokenExpiryMs: number | null;
  expiresInSeconds: number | null;
} {
  if (!data || typeof data !== "object") {
    return { tokenExpiryMs: null, expiresInSeconds: null };
  }

  const payload = data as {
    expiresIn?: unknown;
    sessionTimeoutSemantics?: {
      idle_timeout_seconds?: unknown;
    };
    data?: {
      expiresIn?: unknown;
      sessionTimeoutSemantics?: {
        idle_timeout_seconds?: unknown;
      };
    };
  };

  const idleTimeoutSeconds = extractPositiveInteger(
    payload.sessionTimeoutSemantics?.idle_timeout_seconds ??
      payload.data?.sessionTimeoutSemantics?.idle_timeout_seconds,
  );
  const expiresInSeconds =
    idleTimeoutSeconds ??
    extractPositiveInteger(payload.expiresIn ?? payload.data?.expiresIn);

  return {
    tokenExpiryMs: expiresInSeconds
      ? Date.now() + expiresInSeconds * 1000
      : null,
    expiresInSeconds,
  };
}

function persistRefreshedSession(payload: unknown): void {
  const { tokenExpiryMs, expiresInSeconds } = extractRefreshPayload(payload);
  const nextTokenExpiryMs = tokenExpiryMs ?? sessionExpiryHintMs;
  setSessionRefreshState({
    isAuthenticated: true,
    tokenExpiryMs: nextTokenExpiryMs,
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("app:session-refreshed", {
        detail: {
          tokenExpiryMs: nextTokenExpiryMs,
          expiresInSeconds,
        },
      }),
    );
  }
}

apiClient.interceptors.response.use(
  (response) => {
    try {
      logger.debug(
        `[api][response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
        {
          status: response.status,
          data: response.data,
        },
      );
    } catch {
      // ignore
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ApiRequestConfig;
    if (error.response?.status === 429 && originalRequest) {
      const delay = quotaRetryDelay(originalRequest.method, originalRequest._quotaRetries || 0,
        getHeaderValue(error.response.headers, "Retry-After"));
      if (delay != null) {
        originalRequest._quotaRetries = (originalRequest._quotaRetries || 0) + 1;
        await new Promise<void>((resolve, reject) => {
          const signal = originalRequest.signal;
          const abort = () => { clearTimeout(timer); signal?.removeEventListener?.("abort", abort); reject(error); };
          const timer = setTimeout(() => { signal?.removeEventListener?.("abort", abort); resolve(); }, delay);
          if (signal?.aborted) abort();
          else signal?.addEventListener?.("abort", abort);
        });
        return apiClient(originalRequest);
      }
    }
    const currentPath = window.location.pathname || "";
    const hasStoredSession = authenticatedSessionHint;
    const payload = error.response?.data as Record<string, unknown> | undefined;

    if (
      error.response?.status === 423 &&
      String(payload?.code || payload?.error || "") === "SESSION_LOCKED"
    ) {
      try {
        window.dispatchEvent(
          new CustomEvent("app:session-locked", {
            detail: {
              url: originalRequest?.url,
              method: originalRequest?.method,
              message: payload?.message,
            },
          }),
        );
      } catch {
        // ignore event dispatch failures
      }
      return Promise.reject(error);
    }

    // [DEBUG] Log every 401 — capture the interceptor's decision factors
    const is401 = error.response?.status === 401;
    if (is401) {
      const willAttemptRefresh =
        !originalRequest._retry &&
        !originalRequest.url?.includes("/users/refresh-token") &&
        hasStoredSession &&
        !isAuthOrPublicRoute(currentPath);

      logger.debug("[api][401-interceptor] 401 received — diagnostic", {
        url: originalRequest.url,
        method: originalRequest.method,
        hasStoredSession,
        authenticatedSessionHint,
        refreshRetryBlocked: refreshRetryBlockedUntilMs > Date.now(),
        refreshRetryBlockedUntil: refreshRetryBlockedUntilMs
          ? new Date(refreshRetryBlockedUntilMs).toISOString()
          : null,
        refreshRetryBlockReason,
        isAuthRoute: isAuthOrPublicRoute(currentPath),
        currentPath,
        _retry: originalRequest._retry,
        willAttemptRefresh,
        isRefreshing,
        hasRefreshPromise: Boolean(refreshPromise),
        responseStatus: error.response?.status,
        responseStatusText: error.response?.statusText,
      });
    }

    if (
      is401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/users/refresh-token") &&
      hasStoredSession &&
      !isAuthOrPublicRoute(currentPath)
    ) {
      if (refreshRetryBlockedUntilMs > Date.now()) {
        const retryAfterSeconds = Math.ceil(
          (refreshRetryBlockedUntilMs - Date.now()) / 1000,
        );
        logger.warn(
          "[api][refresh] Skipping refresh attempt during Retry-After cooldown",
          {
            retryAfterSeconds,
            reason: refreshRetryBlockReason || "refresh_rate_limited",
          },
        );
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      if (!isRefreshing) {
        refreshPromise = startRefreshRequest();
        try {
          const refreshResponse = await refreshPromise;
          persistRefreshedSession(
            (refreshResponse as AxiosResponse | undefined)?.data,
          );
          clearRefreshRetryCooldown();
          onRefreshed();
          // Retry the original request that triggered the refresh
          return apiClient(originalRequest);
        } catch (e) {
          const refreshError = e as AxiosError;
          if (refreshError.response?.status === 429) {
            const retryAfterSeconds =
              extractRetryAfterSeconds(refreshError) || 60;
            setRefreshRetryCooldown(retryAfterSeconds, "refresh_rate_limited");
            subscribers.length = 0;
            logger.warn(
              "[api][refresh] Refresh endpoint rate-limited; honoring Retry-After cooldown",
              { retryAfterSeconds },
            );
            return Promise.reject(error);
          }

          // Refresh failed, redirect to login or bubble up
          subscribers.length = 0;
          clearSessionRefreshState();
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("tokenExpiry");

          // Prevent infinite reload loops when already on auth pages
          if (!authRedirectInProgress && !isAuthOrPublicRoute(currentPath)) {
            authRedirectInProgress = true;
            window.location.assign("/login");
            // Reset flag eventually in case navigation is blocked by tests/tools
            setTimeout(() => {
              authRedirectInProgress = false;
            }, 3000);
          }
          return Promise.reject(error);
        }
      } else if (refreshPromise) {
        // Queue behind the in-flight refresh
        try {
          await refreshPromise;
        } catch {
          return Promise.reject(error);
        }
        return apiClient(originalRequest);
      }
    }
    // Centralized: dispatch an application-level event for permission errors (403).
    // Do NOT dispatch for auth workflow endpoints/codes (e.g. first-login password change required),
    // which are expected control-flow responses rather than RBAC denials.
    const isLogout = originalRequest?.url?.includes("/users/logout");
    const requestUrl = String(originalRequest?.url || "");
    const isAuthWorkflowEndpoint =
      requestUrl.includes("/users/login") ||
      requestUrl.includes("/api/users/login") ||
      requestUrl.includes("/auth/change-password-first-login") ||
      requestUrl.includes("/users/refresh-token");
    // Auto-retry once on CSRF token expiry — fetch a fresh token then replay.
    // Uses refreshCsrfToken() for deduplication so concurrent failures don't
    // create conflicting _csrf secrets.
    if (
      error.response?.status === 403 &&
      payload?.code === "CSRF_VALIDATION_FAILED" &&
      !(originalRequest as typeof originalRequest & { _csrfRetry?: boolean })
        ._csrfRetry
    ) {
      (
        originalRequest as typeof originalRequest & { _csrfRetry?: boolean }
      )._csrfRetry = true;
      try {
        const freshToken = await refreshCsrfToken();
        if (typeof freshToken === "string" && freshToken.length > 0) {
          (originalRequest.headers as Record<string, string>)["x-csrf-token"] =
            freshToken;
        }
        return apiClient(originalRequest);
      } catch {
        // CSRF refresh failed — fall through to normal error handling
      }
    }

    const authWorkflowCodes = new Set([
      "PASSWORD_CHANGE_REQUIRED",
      "PASSWORD_LOGIN_DISABLED",
      "AUTH_METHOD_NOT_ALLOWED",
      "AUTH_POLICY_UNAVAILABLE",
    ]);
    const isAuthWorkflowCode =
      authWorkflowCodes.has(String(payload?.error || "")) ||
      authWorkflowCodes.has(String(payload?.code || ""));

    if (
      error.response?.status === 403 &&
      !isLogout &&
      !isAuthWorkflowEndpoint &&
      !isAuthWorkflowCode
    ) {
      try {
        const currentPath =
          typeof window !== "undefined" ? window.location.pathname : "";
        const normalizedMethod = String(originalRequest?.method || "").toLowerCase();
        const normalizedUrl = String(originalRequest?.url || "");
        const suppressGlobalNotice =
          (currentPath.startsWith("/patients") &&
            normalizedMethod === "get" &&
            /^\/patients(?:\?.*)?$/.test(normalizedUrl)) ||
          (normalizedMethod === "get" &&
            normalizedUrl.includes("/admin/demo-orgs/") &&
            normalizedUrl.includes("/status")) ||
          (currentPath.startsWith("/worklist") &&
            normalizedMethod === "get" &&
            normalizedUrl.includes("/dashboard/worklist")) ||
          (currentPath.startsWith("/reports/outcomes") &&
            normalizedMethod === "get" &&
            normalizedUrl.includes("/analytics/outcomes") &&
            /scope .*not permitted|not permitted for your role/i.test(
              String(payload?.message || payload?.error || ""),
            )) ||
          (normalizedMethod === "get" &&
            (currentPath.startsWith("/admin/billing") ||
              currentPath.startsWith("/billing/org-commercial")) &&
            (normalizedUrl.includes("/billing/org-commercial/payments") ||
              /\/billing\/org-commercial\/invoices\/[^/?]+/.test(
                normalizedUrl,
              )));
        const info = {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status: error.response?.status,
          suppressGlobalNotice,
          response: error.response?.data,
        };
        window.dispatchEvent(
          new CustomEvent("app:permission-required", { detail: info }),
        );
        // If the backend indicates MFA is required, emit a dedicated event
        try {
          const innerPayload = error.response?.data as
            | Record<string, unknown>
            | undefined;
          if (innerPayload && innerPayload.mfaRequired) {
            window.dispatchEvent(
              new CustomEvent("app:mfa-required", {
                detail: { setupUrl: payload?.setupUrl || "/mfa/setup" },
              }),
            );
          }
        } catch {
          // ignore malformed payload
        }
      } catch {
        // ignore
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Named export of the API client
 * Use this if you prefer named imports over default imports
 */
export { apiClient };

/**
 * Legacy named export for backward compatibility
 * @deprecated Use `apiClient` instead. This alias will be removed in v2.0
 */
import type { AxiosInstance } from "axios";
export const api: AxiosInstance = apiClient;

/**
 * Default export of the API client (RECOMMENDED)
 *
 * This is the main HTTP client for the application with full security features.
 * Always use this for API requests to ensure CSRF protection and token refresh.
 *
 * @example
 * ```typescript
 * import apiClient from '@shared/services/apiClient';
 * const response = await apiClient.get('/users');
 * ```
 */
// Named, capitalized export for consumers that prefer explicit `APIClient` symbol
export const APIClient = apiClient;
export default apiClient;
